import { getItemFromStorage } from '@utils/localStorageHandler';

declare const IS_DEV: boolean;

/**
 * Registry of localStorage-driven feature flags.
 *
 * Each key here is a value that testers/devs can set in browser localStorage
 * to `'true'` (as a string) to opt in to a hidden feature. Descriptions
 * document what the flag controls; keep them short.
 *
 * Setting a flag in the browser console:
 *   localStorage.setItem('ENABLE_ANALYTICS_PREVIEW', 'true')
 */
export const FEATURE_FLAGS = {
  ENABLE_ANALYTICS_PREVIEW:
    'Concierge: analytics preview UI in campaign builder / campaigns table. Local-only (not auto-enabled in dev).',
  ENABLE_PRESS_INQUIRIES:
    'DIY App: Press Inquiries sidebar link. Local-only (not auto-enabled in dev).',
  ENABLE_DIRECT_WISE_WITHDRAWAL:
    'Publisher: direct Wise-to-Wise withdrawal method in payout method selection. Temporarily hidden until cases are aligned with Wise Support; bank transfer (by Wise) stays enabled. Local-only (not auto-enabled in dev).',
  ENABLE_AD_ATTRIBUTION:
    'All standalone realms: first-party ad attribution capture at landing (first/last touch, sent at signup). Auto-enabled in dev; enable on prod after TRACK-010 E2E verification.',
  GOD_MODE:
    'All realms: verbose request / response / stream console dumps (see @utils/debugMode). Auto-enabled in dev. Compiled out of production bundles unless RESPONA_ALLOW_VERBOSE_DEBUG=true at build time.',
} as const;

export type FeatureFlagKey = keyof typeof FEATURE_FLAGS;

type Options = {
  /**
   * When true (default), dev builds (`IS_DEV`) see the feature automatically.
   * Set to false for flags that must be explicitly opted in on every browser,
   * e.g. features being tested in prod with a specific cohort where devs
   * shouldn't see the unfinished UI.
   */
  includeDev?: boolean;
};

/**
 * Returns whether the given localStorage-gated feature should be visible.
 * See `FEATURE_FLAGS` for the registry of known keys.
 */
export const isFeatureEnabled = (
  key: FeatureFlagKey,
  { includeDev = true }: Options = {}
): boolean => {
  if (includeDev && IS_DEV) return true;
  return getItemFromStorage(key, false) === 'true';
};
